import React, { useMemo, useState, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  IconButton,
  Collapse,
  Typography,
  MenuItem,
  Button,
  Chip,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChatIcon from "@mui/icons-material/Chat";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DeleteIcon from "@mui/icons-material/Delete";

// Mock data for documents with revisions
// const documentData = [
//   {
//     document: "Procurement of Tank Plates",
//     docNum: "001",
//     revNum: "3",
//     docLink: "https://example.com/project-plan",
//     progress: 85,
//     responsible: "Omar Mokhtar",
//     revisions: [
//       { version: "Rev 1", date: "2023-01-01", changes: "Initial draft" },
//       { version: "Rev 2", date: "2023-02-15", changes: "Added milestones" },
//       { version: "Rev 3", date: "2023-03-10", changes: "Finalized content" },
//     ],
//   },
//   {
//     document: "Procurement of Nozzles",
//     docNum: "002",
//     revNum: "2",
//     docLink: "https://example.com/budget-overview",
//     progress: 90,
//     responsible: "Omar Mokhtar",
//     revisions: [
//       { version: "Rev 1", date: "2023-01-20", changes: "Initial draft" },
//       {
//         version: "Rev 2",
//         date: "2023-02-10",
//         changes: "Updated budget figures",
//       },
//     ],
//   },
// ];

const RACITable = ({
  data,
  handleButtonClick,
  allUsers,
  currentUser,
  openVersionNewModal,
  openNewRaciModal,
  getVersions,
  refreshVersionsRef,
  updateRaciData,
  plant = false,
  selectedDocuments,
  setSelectedDocuments,
  onDeleteRaci,
}) => {
  const racis = data || [];
  const [expandedRows, setExpandedRows] = useState({});
  const [versionsData, setVersionsData] = useState({});
  const [loadingVersions, setLoadingVersions] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  console.log("rr", racis);

  // Prefetch versions for all RACIs when data loads
  React.useEffect(() => {
    if (racis.length > 0 && getVersions) {
      // Fetch versions for all RACIs in parallel
      const fetchAllVersions = async () => {
        const raciIds = racis.map((raci) => raci.id).filter(Boolean);
        if (raciIds.length === 0) return;

        const versionPromises = raciIds.map(async (raciId) => {
          try {
            const versions = await getVersions(raciId);
            return { raciId, versions };
          } catch (error) {
            console.error(`Error fetching versions for RACI ${raciId}:`, error);
            return { raciId, versions: [] };
          }
        });

        const results = await Promise.all(versionPromises);
        setVersionsData((prev) => {
          const newVersionsData = { ...prev };
          results.forEach((result) => {
            newVersionsData[result.raciId] = result.versions;
          });
          return newVersionsData;
        });
      };

      fetchAllVersions();
    }
  }, [racis, getVersions]); // Only depend on racis and getVersions

  // Expose refresh function via ref so parent can call it after adding version
  React.useEffect(() => {
    if (refreshVersionsRef && getVersions) {
      refreshVersionsRef.current = (raciId) => {
        // Refetch versions for this RACI after adding a new version
        if (raciId) {
          getVersions(raciId)
            .then((versions) => {
              setVersionsData((prev) => ({ ...prev, [raciId]: versions }));
            })
            .catch((error) => {
              console.error("Error fetching versions:", error);
              setVersionsData((prev) => ({ ...prev, [raciId]: [] }));
            });
        }
      };
    }
  }, [getVersions, refreshVersionsRef]);

  const toggleRow = (rowId, raciId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
    // Versions are already prefetched, so no need to fetch here
  };

  const columns = useMemo(() => {
    const projectColumn = plant
      ? [
          {
            accessorKey: "projectName",
            header: "Project",
            size: 140,
            Cell: ({ cell }) => cell.getValue() || "-",
          },
        ]
      : [];

    return [
      ...projectColumn,
      {
        id: "document",
        header: "Document Info",
        columns: [
          {
            accessorKey: "docNum",
            header: "Doc #",
            size: 10, // Reduced width
          },
          {
            accessorKey: "document",
            header: "Description",
            size: 120, // Reduced width
            Cell: ({ cell, row }) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                {/* <IconButton
                  onClick={() => toggleRow(row.index, row.original.id)}
                >
                  {expandedRows[row.index] ? (
                    <KeyboardArrowUp />
                  ) : (
                    <KeyboardArrowDown />
                  )}
                </IconButton> */}
                {cell.getValue()}
              </Box>
            ),
          },
          {
            accessorKey: "revNum",
            header: "Rev #",
            size: 10, // Reduced width
            enableEditing: (row) =>
              currentUser
                ? row.original?.task_members?.includes(currentUser)
                : false,
          },
          {
            accessorKey: "docLink",
            header: "Link",
            size: 60, // Reduced width
            Cell: ({ cell }) => (
              <a
                href={cell.getValue()}
                target="_blank"
                rel="noopener noreferrer"
              >
                Link
              </a>
            ),
          },
          {
            accessorKey: "docTransmittalNumber",
            header: "Doc Transmittal #",
            size: 10,
          },
          {
            accessorKey: "progress",
            header: "Progress",
            size: 5, // Reduced width
            Cell: ({ cell, row }) => {
              const timesheets = row.original?.timesheets || [];
              let progress = timesheets.reduce(
                (acc, t) => acc + t?.progress,
                0
              );
              return `${progress}%`;
            },
          },
        ],
      },
      // {
      //   accessorKey: "Approved",
      //   header: "Approved",
      //   enableColumnActions: false,
      //   enableSorting: false,
      //   size: 70, // Reduced width
      //   muiEditTextFieldProps: () => ({
      //     children: allUsers?.map((user) => (
      //       <MenuItem key={user.email} value={user.email}>
      //         {user.email}
      //       </MenuItem>
      //     )),
      //     select: true,
      //   }),
      // },
      {
        accessorKey: "responsible",
        header: "R",
        enableColumnActions: false,
        enableSorting: false,
        size: 70, // Reduced width
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        // Extract email from responsible object for display and editing
        accessorFn: (row) => {
          if (typeof row.responsible === "object" && row.responsible?.email) {
            return row.responsible.email;
          }
          return row.responsible || "";
        },
        Cell: ({ cell, row }) => {
          const responsible = row.original.responsible;
          const label =
            typeof responsible === "object" && responsible
              ? responsible.name || responsible.email
              : cell.getValue();

          if (!label) return "-";

          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Chip
                size="small"
                label={label}
                // keep as display-only for now; editing is still via inline edit
                variant="outlined"
              />
            </Box>
          );
        },
        muiEditTextFieldProps: () => ({
          children: allUsers?.map((user) => (
            <MenuItem key={user.email} value={user.email}>
              {user.email}
            </MenuItem>
          )),
          select: true,
        }),
      },
      {
        accessorKey: "accountable",
        header: "A",
        enableEditing: false,
        size: 100, // Reduced width
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.accountable || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("acc", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("acc", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "consulted",
        header: "C",
        size: 100, // Reduced width
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.consulted || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("cons", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("cons", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "informed",
        header: "I",
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 60, // Reduced width
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.informed || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("inf", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("inf", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "mail",
        header: "Mail",
        enableColumnActions: false,
        enableSorting: false,
        enableEditing: false,
        size: 60, // Reduced width
        Cell: ({ row }) => (
          <IconButton onClick={() => handleButtonClick("mail", row)}>
            <AttachEmailIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "chat",
        header: "Chat",
        enableColumnActions: false,
        enableSorting: false,
        enableEditing: false,
        size: 60, // Reduced width
        Cell: ({ row }) => (
          <IconButton onClick={() => handleButtonClick("comments", row)}>
            <ChatIcon />
          </IconButton>
        ),
      },
      // {
      //   accessorKey: "timesheet",
      //   header: "Timesheet",
      //   enableEditing: false,
      //   size: 70, // Reduced width
      //   Cell: ({ row }) => (
      //     <IconButton onClick={() => handleButtonClick("timesheet", row)}>
      //       <PostAddIcon />
      //     </IconButton>
      //   ),
      // },
      {
        accessorKey: "delete",
        header: "Delete",
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 70,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => (
          <IconButton
            onClick={() => onDeleteRaci && onDeleteRaci(row.original)}
            color="error"
            aria-label="delete"
          >
            <DeleteIcon />
          </IconButton>
        ),
      },
      {
        id: "versions",
        accessorKey: "versions",
        header: "Versions",
        size: 200,
        Cell: ({ row }) => {
          const raciId = row.original.id;
          const versions = versionsData[raciId] || [];
          // Check if versions are still being loaded (for this specific RACI)
          const isLoading =
            versions.length === 0 &&
            raciId &&
            !versionsData.hasOwnProperty(raciId);

          return (
            <Collapse in={expandedRows[row.index]} timeout="auto" unmountOnExit>
              <Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={openVersionNewModal.bind(this, raciId)}
                  sx={{ mb: 2 }}
                >
                  Add Version
                </Button>
                {versions.length === 0 ? (
                  <Typography color="textSecondary">
                    No versions available
                  </Typography>
                ) : (
                  versions.map((rev, index) => (
                    <Box key={rev.id || index} sx={{ margin: "1rem 0" }}>
                      <Typography>
                        <strong>Version:</strong> {rev.version}
                      </Typography>
                      <Typography>
                        <strong>Date:</strong> {rev.date}
                      </Typography>
                      <Typography>
                        <strong>Changes:</strong> {rev.changes}
                      </Typography>
                      {rev.link && (
                        <a
                          href={rev.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Link
                        </a>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>
          );
        },
      },
    ];
  }, [
    expandedRows,
    currentUser,
    allUsers,
    handleButtonClick,
    plant,
    updateRaciData,
    onDeleteRaci,
    versionsData,
    openVersionNewModal,
  ]);

  // Sync selectedDocuments with rowSelection when it changes
  React.useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );
    const selectedDocs = racis.filter((raci) =>
      selectedIds.includes(String(raci.id))
    );
    setSelectedDocuments(selectedDocs);
  }, [rowSelection, racis, setSelectedDocuments]);

  const table = useMaterialReactTable({
    columns,
    data: racis || [],
    // enableColumnOrdering: true,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    paginationDisplayMode: "pages",
    enableEditing: true,
    onEditingRowSave: async ({ values, table, row }) => {
      const raciId = row.original.id;
      // Handle responsible field - if it's an email string, keep it; if it's an object, extract email
      const responsibleValue =
        typeof values.responsible === "string"
          ? values.responsible
          : values.responsible?.email || row.original.responsible?.email || "";

      const updateData = {
        ...values,
        responsible: responsibleValue,
      };

      if (updateRaciData && raciId) {
        await updateRaciData(raciId, updateData);
      }
      table.setEditingRow(null);
    },
    renderTopToolbarCustomActions: () => {
      return (
        <Button onClick={openNewRaciModal} variant="contained">
          Add Document
        </Button>
      );
    },
  });

  return <MaterialReactTable table={table} />;
};

export default RACITable;
